import React, {useEffect} from 'react';
import {Box, Button, Stack, TextField} from '@mui/material';
import {LoadingButton} from '@mui/lab';
import {Controller, useForm} from 'react-hook-form';
import {z} from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';
import {StyleSx, Sx} from 'app/types/common';
import {CouponState} from 'app/components/BillingManager/hooks/useCoupon';

const buttonSx: StyleSx = {height: 36, minWidth: 76};

const scheme = z.object({coupon: z.string().trim()});

type FormValues = z.infer<typeof scheme>;

interface Props extends Sx {
  state: CouponState;
  autoFocus?: boolean;
  onChange: (value: string) => void;
  onValidate: (value: string) => Promise<void>;
}

export function ValidateCoupon({sx, state, autoFocus, onChange, onValidate}: Props) {
  const {control, watch, reset, handleSubmit} = useForm<FormValues>({
    defaultValues: {coupon: ''},
    resolver: zodResolver(scheme),
  });

  const coupon = watch('coupon');

  useEffect(() => {
    onChange(coupon);
  }, [coupon, onChange]);

  const handleValidate = async (data: FormValues) => {
    await onValidate(data.coupon);
  };

  return (
    <Box sx={sx} component="form" onSubmit={handleSubmit(handleValidate)}>
      <Stack direction="row" alignItems="flex-start" gap={2}>
        <Controller
          control={control}
          name="coupon"
          render={({field}) => (
            <TextField
              sx={{width: 300}}
              {...field}
              inputProps={{'data-id': 'voucher-input'}}
              InputProps={{readOnly: state.type === 'checking' || state.type === 'valid'}}
              variant="standard"
              placeholder="Enter a voucher code"
              autoFocus={autoFocus}
              error={state.type === 'invalid'}
            />
          )}
        />

        {state.type === 'valid' || state.type === 'invalid' ? (
          <Button
            data-id="clear-button"
            sx={buttonSx}
            type="reset"
            variant="outlined"
            onClick={() => reset()}
          >
            Clear
          </Button>
        ) : (
          <LoadingButton
            data-id="validate-button"
            sx={buttonSx}
            type="submit"
            variant="outlined"
            loading={state.type === 'checking'}
            disabled={state.type === 'empty'}
          >
            Validate
          </LoadingButton>
        )}
      </Stack>
    </Box>
  );
}
